
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    IconButton,
    Paper,
} from "@material-ui/core";
import { Search, DeleteOutline } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal";

const TagContactsModal = ({ open, onClose, tag, reloadTags }) => {
    const history = useHistory();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchParam, setSearchParam] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);

    const loadMore = React.useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/contacts", {
                params: { searchParam, pageNumber, tagId: tag.id },
            });
            setContacts((prevState) => [...prevState, ...data.contacts]);
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, [searchParam, pageNumber, tag]);

    useEffect(() => {
        if (open && tag) {
            setContacts([]);
            setPageNumber(1);
            setHasMore(false);
        }
    }, [open, tag]);

    useEffect(() => {
        if (open && tag) {
            loadMore();
        }
    }, [open, tag, pageNumber, loadMore]); // Include loadMore in dependencies

    const handleSearch = (event) => {
        setSearchParam(event.target.value);
        setPageNumber(1);
        setContacts([]);
    };

    const loadMoreContacts = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMoreContacts();
        }
    };

    const handleRemoveTag = async () => {
        try {
            await api.delete(`/contacts/${selectedContact.id}/tags/${tag.id}`);
            setContacts((prevState) =>
                prevState.filter((c) => c.id !== selectedContact.id)
            );
            toast.success(i18n.t("tags.toasts.deleted")); // Reusing deleted toast or create a new one
            if (reloadTags) reloadTags();
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                {tag?.name} - {i18n.t("contactModal.form.tags")}
            </DialogTitle>
            <DialogContent dividers>
                <Paper variant="outlined" style={{ padding: 8, marginBottom: 8 }}>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Paper>
                <Paper
                    variant="outlined"
                    onScroll={handleScroll}
                    style={{ height: 400, overflowY: "scroll" }} // Adjust height as needed
                >
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t("contacts.table.name")}</TableCell>
                                    <TableCell>{i18n.t("contacts.table.whatsapp")}</TableCell>
                                    <TableCell>{i18n.t("contacts.table.actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contacts.map((contact) => (
                                    <TableRow key={contact.id}>
                                        <TableCell>{contact.name}</TableCell>
                                        <TableCell>{contact.number}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSelectedContact(contact);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRow><TableCell colSpan={3} align="center">Carregando...</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {i18n.t("common.close")}
                </Button>
            </DialogActions>

            <ConfirmationModal
                title={i18n.t("tags.toasts.deleted")}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={handleRemoveTag}
            >
                {i18n.t("tags.toasts.deleted")}
            </ConfirmationModal>
        </Dialog>
    );
};

export default TagContactsModal;
